import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  TrendingUp, Sparkles, BarChart3, Wallet, Shield, Home, 
  CheckCircle2, AlertCircle, ArrowRight 
} from 'lucide-react';
import { formatCurrency, safeNumber } from '../utils/formatters';
import { 
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip 
} from 'recharts';

/**
 * PrintAnalysis - A print-optimized version of the ResultsDashboard
 * This page is rendered by Playwright and converted to PDF
 * It receives analysis data via URL search params (base64 encoded)
 */
const PrintAnalysis = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [inputData, setInputData] = useState(null);
  const [userName, setUserName] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      const analysisParam = searchParams.get('analysis');
      const inputParam = searchParams.get('input');
      const nameParam = searchParams.get('name');
      
      if (analysisParam) {
        const decodedAnalysis = JSON.parse(atob(analysisParam));
        setData(decodedAnalysis);
      }
      
      if (inputParam) {
        const decodedInput = JSON.parse(atob(inputParam));
        setInputData(decodedInput);
      }
      
      if (nameParam) {
        setUserName(decodeURIComponent(nameParam));
      }
      
      window.pdfReady = true;
    } catch (err) {
      console.error('Error parsing data:', err);
      setError('Fehler beim Laden der Daten');
    }
  }, [searchParams]);

  if (error) {
    return <div className="p-8 text-center text-red-500">{error}</div>;
  }

  if (!data) {
    return <div className="p-8 text-center text-slate">Lade Analyse...</div>;
  }

  // Extract analysis data
  const analysis = data.retirement_gap_analysis || data.retirement_gap || {};
  
  // Values from analysis
  const gesamtRente = safeNumber(analysis.total_retirement_income_monthly, 0);
  const prognostiziertesKapital = safeNumber(analysis.projected_capital_at_retirement, 0);
  const gesetzlicheRente = safeNumber(analysis.statutory_pension_net_monthly, 0);
  const privateRente = safeNumber(analysis.private_retirement_income_monthly, 0);
  
  // Input data
  const resolvedInput = inputData || data.input_data || {};
  const currentAge = resolvedInput?.current_age || 30;
  const retirementAge = resolvedInput?.retirement_age || 67;
  const monthlySavings = resolvedInput?.monthly_savings || 250;
  const yearsToRetirement = retirementAge - currentAge;
  
  // Financial foundations
  const hasNotgroschen = resolvedInput?.has_notgroschen || false;
  const hasAbsicherung = resolvedInput?.has_income_protection || false;
  const hasImmobilie = resolvedInput?.has_real_estate || false;

  // Strategy calculations (matching website exactly)
  const taxOptimizationBonus = 1.22;
  const coordinationBonus = 1.45;
  const withdrawalRate = 0.035;
  
  const finalCurrent = prognostiziertesKapital;
  const finalTaxOptimized = Math.round(finalCurrent * taxOptimizationBonus);
  const finalCoordinated = Math.round(finalCurrent * coordinationBonus);
  
  const currentMonthlyPension = Math.round((finalCurrent * withdrawalRate) / 12) + gesetzlicheRente + privateRente;
  const taxOptimizedMonthlyPension = Math.round((finalTaxOptimized * withdrawalRate) / 12) + gesetzlicheRente + privateRente;
  const coordinatedMonthlyPension = Math.round((finalCoordinated * withdrawalRate) / 12) + gesetzlicheRente + privateRente;

  const pensionImprovement = coordinatedMonthlyPension - currentMonthlyPension;
  const capitalImprovement = finalCoordinated - finalCurrent;
  const percentageImprovement = Math.round((capitalImprovement / finalCurrent) * 100);

  // Chart data
  const estimatedStartCapital = 30000;
  const generateProjection = (finalValue, years) => {
    const data = [];
    for (let year = 0; year <= years; year++) {
      const progress = year / years;
      const value = estimatedStartCapital * Math.pow(finalValue / estimatedStartCapital, progress);
      data.push({ year, capital: Math.round(value) });
    }
    return data;
  };

  const currentProjection = generateProjection(finalCurrent, yearsToRetirement);
  const taxOptimizedProjection = generateProjection(finalTaxOptimized, yearsToRetirement);
  const coordinatedProjection = generateProjection(finalCoordinated, yearsToRetirement);

  const chartData = currentProjection.map((item, index) => ({
    year: item.year,
    current: item.capital,
    taxOptimized: taxOptimizedProjection[index].capital,
    coordinated: coordinatedProjection[index].capital,
  }));

  const formatCurrencyShort = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${Math.round(value / 1000)}k`;
    return `${Math.round(value)}`;
  };

  const today = new Date().toLocaleDateString('de-DE', { 
    day: '2-digit', month: '2-digit', year: 'numeric' 
  });

  // Scenarios for cards
  const scenarios = [
    {
      id: 'current',
      label: 'Heutige Strategie',
      sublabel: 'Unkoordinierte Anlage',
      capital: finalCurrent,
      pension: currentMonthlyPension,
      color: '#94A3B8',
      bgClass: 'bg-slate-50',
      borderClass: 'border-slate-200',
      icon: BarChart3,
    },
    {
      id: 'taxOptimized',
      label: 'Steueroptimierte Struktur',
      sublabel: 'ETF-Police statt Depot',
      capital: finalTaxOptimized,
      pension: taxOptimizedMonthlyPension,
      color: '#3B82F6',
      bgClass: 'bg-blue-50',
      borderClass: 'border-blue-200',
      icon: TrendingUp,
    },
    {
      id: 'coordinated',
      label: 'Koordinierte Strategie',
      sublabel: 'Vollständig optimiert',
      capital: finalCoordinated,
      pension: coordinatedMonthlyPension,
      color: '#29A366',
      bgClass: 'bg-emerald-50',
      borderClass: 'border-emerald-200',
      icon: Sparkles,
    },
  ];

  // Financial foundations data
  const foundations = [
    {
      id: 'notgroschen',
      name: 'Rücklagen',
      icon: Wallet,
      isActive: hasNotgroschen,
      activeTitle: 'Liquiditätsreserve vorhanden',
      activeDescription: 'Sie haben eine Notreserve angelegt. Prüfen Sie regelmäßig, ob die Höhe noch zu Ihrer Lebenssituation passt.',
      inactiveTitle: 'Keine Liquiditätsreserve',
      inactiveDescription: 'Rücklagen schützen Sie vor unerwarteten Ausgaben und verhindern, dass Sie Investments vorzeitig auflösen müssen.',
      colorActive: 'bg-amber-50 border-amber-200',
      colorInactive: 'bg-amber-50/50 border-amber-100',
      iconActive: 'bg-amber-100 text-amber-600',
      iconInactive: 'bg-amber-100/50 text-amber-500',
    },
    {
      id: 'absicherung',
      name: 'Absicherung',
      icon: Shield,
      isActive: hasAbsicherung,
      activeTitle: 'Einkommensschutz vorhanden',
      activeDescription: 'Sie haben angegeben, abgesichert zu sein. Lassen Sie Ihre bestehenden Versicherungen auf Kosten und Leistung prüfen.',
      inactiveTitle: 'Kein Einkommensschutz',
      inactiveDescription: 'Ohne Berufsunfähigkeitsversicherung riskieren Sie bei Krankheit Ihre gesamte Finanzplanung.',
      colorActive: 'bg-blue-50 border-blue-200',
      colorInactive: 'bg-blue-50/50 border-blue-100',
      iconActive: 'bg-blue-100 text-blue-600',
      iconInactive: 'bg-blue-100/50 text-blue-500',
    },
    {
      id: 'immobilie',
      name: 'Immobilie',
      icon: Home,
      isActive: hasImmobilie,
      activeTitle: 'Immobilie in der Planung',
      activeDescription: 'Eine Immobilie kann Ihre Strategie sinnvoll ergänzen. Wichtig ist die Abstimmung mit Ihrem Gesamtvermögen.',
      inactiveTitle: 'Keine Immobilie geplant',
      inactiveDescription: 'Eigentum kann langfristig Mietkosten sparen und Vermögen aufbauen. Ob es für Sie sinnvoll ist, hängt von vielen Faktoren ab.',
      colorActive: 'bg-emerald-50 border-emerald-200',
      colorInactive: 'bg-emerald-50/50 border-emerald-100',
      iconActive: 'bg-emerald-100 text-emerald-600',
      iconInactive: 'bg-emerald-100/50 text-emerald-500',
    },
  ];

  return (
    <div className="bg-white min-h-screen" id="print-content">
      {/* Header */}
      <header className="px-8 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-outfit text-lg font-bold text-navy">Nico Helfrich</span>
          <span className="text-slate-400">•</span>
          <span className="text-sm text-slate-500">Strategie & Finanzstruktur</span>
        </div>
        <div className="text-sm text-slate-400">Erstellt am {today}</div>
      </header>

      <main className="px-8 py-6">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="font-outfit text-3xl font-bold text-navy mb-2">Ihre Finanzanalyse</h1>
          {userName && <p className="text-slate-500">Erstellt für {userName}</p>}
        </div>

        {/* ============================================ */}
        {/* SECTION 1: Was eine optimierte Strategie verändern kann */}
        {/* ============================================ */}
        <section className="mb-8 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-navy/5 to-cobalt/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cobalt/10 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-cobalt" />
              </div>
              <div>
                <h2 className="font-outfit text-xl font-bold text-navy">Was eine optimierte Strategie verändern kann</h2>
                <p className="text-sm text-slate-600">So könnte sich Ihre Rente mit Optimierung entwickeln</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Strategy Cards - Endkapital */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-navy mb-3">Endkapital bei Renteneintritt</h3>
              <div className="grid grid-cols-3 gap-4">
                {scenarios.map((scenario) => {
                  const Icon = scenario.icon;
                  return (
                    <div
                      key={`capital-${scenario.id}`}
                      className={`p-4 rounded-xl border-2 ${scenario.bgClass} ${scenario.borderClass}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-4 h-4" style={{ color: scenario.color }} />
                        <span className="text-xs text-slate-500">{scenario.sublabel}</span>
                      </div>
                      <div className="text-sm font-medium text-slate-700 mb-1">{scenario.label}</div>
                      <div className="font-mono text-2xl font-bold" style={{ color: scenario.color }}>
                        {formatCurrency(scenario.capital)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Strategy Cards - Monatliche Rente */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-navy mb-3">Monatliche Rente</h3>
              <div className="grid grid-cols-3 gap-4">
                {scenarios.map((scenario) => {
                  const Icon = scenario.icon;
                  return (
                    <div
                      key={`pension-${scenario.id}`}
                      className={`p-4 rounded-xl border-2 ${scenario.bgClass} ${scenario.borderClass}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-4 h-4" style={{ color: scenario.color }} />
                        <span className="text-xs text-slate-500">{scenario.sublabel}</span>
                      </div>
                      <div className="text-sm font-medium text-slate-700 mb-1">{scenario.label}</div>
                      <div className="font-mono text-2xl font-bold" style={{ color: scenario.color }}>
                        {formatCurrency(scenario.pension)}
                      </div>
                      <div className="text-xs text-slate-500">/ Monat</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Optimization Potential Box - Premium Design */}
            <div className="relative overflow-hidden rounded-2xl" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)' }}>
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 opacity-10" style={{ background: 'radial-gradient(circle, #29A366 0%, transparent 70%)' }} />
              <div className="absolute bottom-0 left-0 w-48 h-48 opacity-10" style={{ background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)' }} />
              
              <div className="relative p-8">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-3" style={{ backgroundColor: 'rgba(41, 163, 102, 0.25)' }}>
                    <Sparkles className="w-4 h-4" style={{ color: '#4ADE80' }} />
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#4ADE80' }}>Ihr Optimierungspotenzial</span>
                  </div>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-8">
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3" style={{ backgroundColor: 'rgba(41, 163, 102, 0.2)' }}>
                      <TrendingUp className="w-6 h-6" style={{ color: '#4ADE80' }} />
                    </div>
                    <p className="text-sm mb-2" style={{ color: '#CBD5E1' }}>Mehr Endkapital möglich</p>
                    <p className="font-mono text-4xl font-bold mb-1" style={{ color: '#FFFFFF' }}>+{formatCurrency(capitalImprovement)}</p>
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(41, 163, 102, 0.25)' }}>
                      <ArrowRight className="w-3 h-3 rotate-[-45deg]" style={{ color: '#4ADE80' }} />
                      <span className="text-xs font-semibold" style={{ color: '#4ADE80' }}>+{percentageImprovement}%</span>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3" style={{ backgroundColor: 'rgba(251, 191, 36, 0.2)' }}>
                      <Wallet className="w-6 h-6" style={{ color: '#FBBF24' }} />
                    </div>
                    <p className="text-sm mb-2" style={{ color: '#CBD5E1' }}>Mehr Rente monatlich</p>
                    <p className="font-mono text-4xl font-bold mb-1" style={{ color: '#FFFFFF' }}>+{formatCurrency(pensionImprovement)}</p>
                    <span className="text-xs" style={{ color: '#94A3B8' }}>jeden Monat zusätzlich</span>
                  </div>
                </div>
                
                {/* Footer note */}
                <div className="mt-6 pt-4 text-center" style={{ borderTop: '1px solid rgba(100, 116, 139, 0.3)' }}>
                  <p className="text-sm" style={{ color: '#CBD5E1' }}>
                    Mit einer koordinierten Strategie – bei <span className="font-semibold" style={{ color: '#FFFFFF' }}>gleicher Sparrate von {formatCurrency(monthlySavings)}/Monat</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Page Break - Chart starts on new page */}
        <div style={{ pageBreakBefore: 'always' }} />

        {/* ============================================ */}
        {/* SECTION 2: Kapitalentwicklung Chart */}
        {/* ============================================ */}
        <section className="mb-8 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="font-outfit text-lg font-bold text-navy">Kapitalentwicklung über {yearsToRetirement} Jahre</h2>
          </div>
          <div className="p-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="gradCurrent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#94A3B8" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#94A3B8" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradTaxOptimized" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradCoordinated" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#29A366" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#29A366" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="year" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#64748B' }}
                    tickFormatter={(v) => v === 0 ? 'Heute' : `${v}J`}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fontSize: 9, fill: '#64748B' }}
                    tickFormatter={(v) => formatCurrencyShort(v)}
                    width={40}
                  />
                  {/* Tooltip disabled for PDF - causes rendering issues */}
                  <Area type="monotone" dataKey="current" stroke="#94A3B8" strokeWidth={2} strokeDasharray="5 3" fill="url(#gradCurrent)" />
                  <Area type="monotone" dataKey="taxOptimized" stroke="#3B82F6" strokeWidth={2} fill="url(#gradTaxOptimized)" />
                  <Area type="monotone" dataKey="coordinated" stroke="#29A366" strokeWidth={3} fill="url(#gradCoordinated)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            
            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <svg width="24" height="3"><line x1="0" y1="1.5" x2="24" y2="1.5" stroke="#94A3B8" strokeWidth="2" strokeDasharray="5 3" /></svg>
                <span className="text-xs text-slate-500">Heutige Strategie</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 bg-blue-500 rounded" />
                <span className="text-xs text-blue-600">Steueroptimiert</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-1 bg-emerald-500 rounded" />
                <span className="text-xs text-emerald-600 font-medium">Koordiniert</span>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* SECTION 3: Ihre Finanzbausteine im Check */}
        {/* ============================================ */}
        <section className="mb-8">
          <div className="text-center mb-6">
            <h2 className="font-outfit text-xl font-bold text-navy mb-2">Ihre Finanzbausteine im Check</h2>
            <p className="text-sm text-slate-500">Individuelle Empfehlungen basierend auf Ihrer aktuellen Situation</p>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4">
            {foundations.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.id}
                  className={`rounded-xl p-4 border-2 ${item.isActive ? item.colorActive : item.colorInactive}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.isActive ? item.iconActive : item.iconInactive}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-wider text-slate-500">{item.name}</span>
                      <div className="flex items-center gap-1 mt-0.5">
                        {item.isActive ? (
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                        ) : (
                          <AlertCircle className="w-3 h-3 text-amber-500" />
                        )}
                        <span className={`text-xs font-medium ${item.isActive ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {item.isActive ? 'Vorhanden' : 'Nicht vorhanden'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <h3 className="font-semibold text-navy text-sm mb-1">
                    {item.isActive ? item.activeTitle : item.inactiveTitle}
                  </h3>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    {item.isActive ? item.activeDescription : item.inactiveDescription}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Ganzheitliche Beratung Card */}
          <div className="p-5 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border border-slate-200">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-navy rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-navy mb-1">Ganzheitliche Beratung</h4>
                <p className="text-sm text-slate-600">
                  {hasNotgroschen && hasAbsicherung && hasImmobilie 
                    ? 'Sie haben alle drei Bausteine berücksichtigt. Im Strategiegespräch optimieren wir das Zusammenspiel für maximale Effizienz.'
                    : hasNotgroschen || hasAbsicherung || hasImmobilie
                      ? 'Einige Bausteine sind bereits vorhanden. Im Strategiegespräch identifizieren wir Lücken und Optimierungspotenzial.'
                      : 'Keiner der drei Bausteine ist aktuell aktiv. Das bietet die Chance, von Anfang an eine koordinierte Strategie aufzubauen.'
                  }
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* SECTION 4: CTA Button */}
        {/* ============================================ */}
        <section className="mb-8 text-center">
          <a
            href="https://nicohelfrich.de/strategiegespraech"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 text-white rounded-full font-medium text-lg shadow-lg hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#29A366', textDecoration: 'none' }}
          >
            <Sparkles className="w-5 h-5" />
            Strategiegespräch vereinbaren
            <ArrowRight className="w-4 h-4" />
          </a>
          <p className="text-xs text-slate-500 mt-3">Kostenlos und unverbindlich</p>
        </section>

        {/* Disclaimer */}
        <section className="text-xs text-slate-400 border-t border-slate-200 pt-4">
          <p>
            <strong>Hinweis:</strong> Diese Analyse basiert auf Szenarien und konfigurierbaren Annahmen. 
            Keine Garantie für zukünftige Entwicklungen. Für eine individuelle Beratung vereinbaren Sie 
            bitte ein Strategiegespräch.
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className="px-8 py-4 border-t border-slate-200 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} Nico Helfrich Finanzstrategie
      </footer>
      
      {/* Hide Emergent badge for print/PDF */}
      <style>{`
        /* Hide any external badges */
        [class*="emergent"], 
        [id*="emergent"],
        [data-emergent],
        div[style*="Made with"],
        div[style*="Powered by"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
        
        /* Print-specific styles */
        @media print {
          body * {
            visibility: visible;
          }
          #print-content {
            position: absolute;
            left: 0;
            top: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default PrintAnalysis;
